import React, { useCallback, useEffect, useMemo, useRef } from "react";

import {
  MenuOpen,
  Menu,
  FullscreenExit,
  Fullscreen,
  ExploreOutlined,
  VerticalSplitOutlined,
  WebAssetOutlined,
  SwapHorizontalCircle,
  LockOpen,
  Lock,
  LayersClear,
  Layers,
} from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

import preimageLogo from "assets/images/preimagePointCloudViewer.svg";
import { usePtcldScriptContext } from "contexts/PtcldScriptContext";
import { useLoading } from "contexts/LoadingContext";
import BlurryOverlay from "components/BlurryOverlay";
import ControlPanel from "components/PointCloudToolbar";

interface PointCloudViewProps {
  potreeContainerDiv: React.RefObject<HTMLDivElement>;
  viewerRef: React.RefObject<Potree.Viewer | null>;
}

const PointCloudView = ({
  potreeContainerDiv,
  viewerRef,
}: PointCloudViewProps) => {
  const {
    ifcLoading,
    scriptsLoaded,
    isSplit,
    setIsSplit,
    width,
    setWidth,
    menu,
    setMenuOpen,
    isLock,
    setIsLock,
    isOverlay,
    setIsOverlay,
  } = usePtcldScriptContext();

  const {
    loading: pointcloudLoading,
    setLoading: setPointcloudLoading,
    setProgress: setLoadingProgression,
    progress: loadingProgression,
    blurryEffect,
    setBlurryEffect,
  } = useLoading();

  const isDragging = useRef(false);
  const parentRef = useRef<HTMLDivElement>(null);
  const anyoneLoading =
    (pointcloudLoading || ifcLoading || !scriptsLoaded) && !blurryEffect;
  const allLoading = !pointcloudLoading && !ifcLoading && scriptsLoaded;

  useEffect(() => {
    function handleResize() {
      const potreeContainerDivcurr = potreeContainerDiv.current;
      if (potreeContainerDivcurr) {
        const divWidth = potreeContainerDivcurr.clientWidth;
        setWidth(divWidth / 2);
      }
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [potreeContainerDiv]);

  useMemo(() => {
    if (viewerRef.current && isSplit) {
      viewerRef.current.splitPaneResize(width);
    } else if (viewerRef.current && !isSplit && potreeContainerDiv.current) {
      viewerRef.current.splitPaneResize(potreeContainerDiv.current.clientWidth);
    }
  }, [width, viewerRef, isSplit, potreeContainerDiv]);

  useMemo(() => {
    if (isSplit && viewerRef.current) {
      viewerRef.current.splitPane();
    } else if (!isSplit && viewerRef.current) {
      viewerRef.current.splitPaneCancel();
    }
  }, [isSplit, viewerRef]);

  useEffect(() => {
    if (
      loadingProgression === 100 &&
      viewerRef.current &&
      !isLock &&
      !isSplit
    ) {
      setIsSplit(true);
      setIsLock(true);
      setIsOverlay(false);
      viewerRef.current.splitPaneResize(width);
      viewerRef.current.splitPane();
      viewerRef.current.splitPaneOverlay(false);
    }
  }, [loadingProgression, viewerRef, width]);

  const moveHandler = useCallback(
    (
      e:
        | React.MouseEvent<HTMLDivElement, MouseEvent>
        | React.TouchEvent<HTMLDivElement>
    ) => {
      if (isDragging.current) {
        let clientX;
        if ("touches" in e) {
          clientX = e.touches[0].clientX;
        } else {
          clientX = e.clientX;
        }

        if (potreeContainerDiv.current) {
          const { left } = potreeContainerDiv.current?.getBoundingClientRect();
          const newWidth = clientX - left;
          setWidth(newWidth);
        }
      }
    },
    [potreeContainerDiv]
  );

  const downHandler = useCallback(() => {
    isDragging.current = true;
  }, []);

  const upHandler = useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false;
    }
  }, []);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;

    if (pointcloudLoading || blurryEffect) {
      interval = setInterval(() => {
        const randomInc = Math.ceil(Math.random() * 10);
        setLoadingProgression((prevProgress: number) => {
          if (prevProgress >= 95) {
            if (interval !== undefined) clearInterval(interval);
            return 100;
          }
          return Math.min(prevProgress + randomInc, 95);
        });
      }, 200);
    }

    return () => {
      if (interval !== undefined) {
        clearInterval(interval);
      }
    };
  }, [pointcloudLoading, setLoadingProgression, blurryEffect]);

  useEffect(() => {
    if (loadingProgression === 100) {
      const hideLoaderTimeout = setTimeout(() => {
        if (blurryEffect) {
          setBlurryEffect(false);
          setLoadingProgression(0);
        } else {
          setPointcloudLoading(false);
          setLoadingProgression(0);
        }
      }, 1000);

      return () => clearTimeout(hideLoaderTimeout);
    }
  }, [loadingProgression, blurryEffect, setPointcloudLoading]);

  useEffect(() => {
    if (viewerRef.current) {
      console.log("overlay effect", isOverlay);
      if (isOverlay) {
        viewerRef.current.splitPaneOverlay(true);
      } else {
        viewerRef.current.splitPaneOverlay(false);
      }
    }
  }, [viewerRef, isOverlay]);

  return (
    <Stack
      sx={{ height: "100%", position: "relative" }}
      ref={parentRef}
    >
      {anyoneLoading && (
        <Stack
          height="100%"
          flex={3}
          alignItems="center"
          justifyContent="center"
          sx={{
            backgroundColor: "rgba(0,0,0,0.5)",
            position: "absolute",
            inset: 0,
          }}
        >
          <Stack
            gap={{ xs: 5, sm: 10 }}
            alignItems="center"
            position="absolute"
          >
            <Stack direction={"row"} gap={4} alignItems={"center"}>
              {/* <div id="unity-logo" /> */}
              <Box
                component="img"
                sx={{
                  width: {
                    xs: 150,
                    sm: 200,
                    md: 400,
                  },
                  height: "100%",
                }}
                alt="logo"
                src={preimageLogo}
              />
            </Stack>
            {/* <Typography>Loading Experience: {Math.round(loadingProgression * 100)} %</Typography> */}
            <Stack direction={"column"} gap={2} alignItems={"center"}>
              <LinearProgress
                variant="determinate"
                value={loadingProgression}
                sx={{
                  height: 12,
                  width: {
                    xs: 300,
                    sm: 400,
                    md: 600,
                  },
                  borderRadius: 5,
                }}
              />
              <Typography
                sx={{
                  color: "text.secondary",
                  fontSize: {
                    xs: "0.5rem",
                    md: "0.8rem",
                  },
                  fontWeight: "bold",
                  opacity: ifcLoading ? 1 : 0,
                }}
              >
                Loading IFC Assets...
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      )}
      <Box
        component="div"
        ref={potreeContainerDiv}
        className="potree_container"
        sx={{ opacity: anyoneLoading ? 0 : 1 }}
        onMouseMove={moveHandler}
        onTouchMove={moveHandler}
        onTouchEnd={upHandler}
        onMouseUp={upHandler}
      >
        <Box component="div" id="potree_render_area"></Box>
        <Box
          component="div"
          id="potree_sidebar_container"
          sx={{
            transition: "transform 0.3s ease",
            transform: menu ? "translateX(0)" : "translateX(-5%)",
            display: menu ? "block" : "none",
            width: {
              xs: "280px !important",
              sm: "340px !important",
            },
          }}
        ></Box>
        {isSplit && (
          <Stack
            sx={{
              position: "absolute",
              height: "100%",
              left: width,
              zIndex: 1000,
            }}
          >
            {!isLock && (
              <Stack
                sx={{
                  position: "absolute",
                  height: "100%",
                  left: "50%",
                  width: "0.1rem",
                  backgroundColor: "background.default",
                }}
              >
                <Divider orientation="vertical" flexItem />
              </Stack>
            )}
            <Stack
              sx={{
                position: "absolute",
                top: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              {!isLock && (
                <IconButton
                  sx={{ color: "grey.500" }}
                  onMouseDown={downHandler}
                  onTouchStart={downHandler}
                  onClick={() => setIsOverlay(!isOverlay)}
                >
                  {isOverlay ? (
                    <LayersClear fontSize="large" />
                  ) : (
                    <Layers fontSize="large" />
                  )}
                </IconButton>
              )}
              {!isLock && (
                <IconButton
                  sx={{ color: "grey.500" }}
                  onMouseDown={downHandler}
                  onTouchStart={downHandler}
                >
                  <SwapHorizontalCircle fontSize="large" />
                </IconButton>
              )}
              <IconButton
                sx={{ color: "grey.500" }}
                onClick={() => setIsLock(!isLock)}
              >
                {isLock ? (
                  <LockOpen fontSize="large" />
                ) : (
                  <Lock fontSize="large" />
                )}
              </IconButton>
            </Stack>
          </Stack>
        )}
      </Box>
      {allLoading && <ControlPanel viewerRef={viewerRef} />}
      <BlurryOverlay loading={blurryEffect} />
    </Stack>
  );
};

export default PointCloudView;
